import { CellContext, ColumnDef, HeaderContext } from '@tanstack/react-table'
import { useContext, useMemo, useState } from 'react'

import { DataTable } from '@/components/ui/data-table'
import { useTeam } from 'app/frontend/base/config/context/team'
import { Link } from 'react-router-dom'
import { Trans } from 'react-i18next'
import AddTeamMemberModal from '../../TeamRoster/components/AddTeamMemberModal'
import {
  TeamWishlistItem,
  TeamWishlistItemCharacterWishlist,
} from 'app/frontend/types/custom/TeamWishlistEncounter'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import { sum } from 'lodash'
import TeamWishTooltip from './TeamWishTooltip'
import WishLegacyPriority from 'app/frontend/types/custom/WishLegacyPriority'
import { LootContext } from '../context'
import WishLegacyPriorityDot from 'app/frontend/base/components/WishLegacyPriorityDot'
import TeamMemberLink from 'app/frontend/base/components/TeamMemberLink'
import { useAuth } from 'app/frontend/base/config/context/auth'

type Props = {
  teamWishlist: wowaudit.TeamWishlist
}

type EncounterItemWithWishes = Omit<TeamWishlistItem, 'wishlists'> &
  TeamWishlistItemCharacterWishlist

type CharacterEncounterWishOverview = wowaudit.CharacterReference & {
  encounters: {
    id: number | null
    items: EncounterItemWithWishes[]
  }[]
  totalScore: number
}

const TeamWishlistTable = ({ teamWishlist }: Props) => {
  const { team } = useTeam()
  const { accessLevel } = useAuth()
  const [addingMembers, setAddingMembers] = useState(false)
  const { valueDisplay } = useContext(LootContext)

  const tableData = useMemo((): CharacterEncounterWishOverview[] => {
    return (team.members ?? [])
      ?.filter((member) => member.teamRank?.wishlist_visibility)
      .map((member) => {
        const encounters = teamWishlist.encounters.map((encounter) => {
          const items = encounter.items.reduce((acc, item) => {
            const characterWishlist = item.wishlists.find(
              (wishlist) =>
                wishlist.character_id === member.characterReference.id,
            )

            if (characterWishlist) {
              acc.push({ ...item, ...characterWishlist })
            }
            return acc
          }, [] as EncounterItemWithWishes[])

          return {
            id: encounter.id,
            items,
          }
        })

        const totalScore = sum(
          encounters.flatMap((encounter) =>
            encounter.items.map((item) => {
              const property =
                valueDisplay === 'percentage' ? 'percentage' : 'score'

              if (!isNaN(item.score_by_spec.total[property] as number)) {
                return item.score_by_spec.total[property]
              } else return 0
            }),
          ),
        )

        return {
          ...member.characterReference,
          encounters,
          totalScore,
        }
      })
  }, [team.members, teamWishlist.encounters, valueDisplay])

  const columns: ColumnDef<CharacterEncounterWishOverview>[] = useMemo(
    () => [
      {
        // For sorting only, not visible in the table
        id: 'name',
        accessorKey: 'name',
      },
      {
        accessorKey: 'totalScore',
        id: 'totalScore',
        size: 175,
        header: ({ table, column }) => (
          <div className='flex items-center gap-1'>
            <span
              className='cursor-pointer'
              onClick={() => {
                const currentSorting = table.getState().sorting
                const nameSort = currentSorting.find(
                  (sort) => sort.id === 'name',
                )
                const isCurrentlyAsc = !nameSort?.desc
                table.setSorting([
                  { id: 'name', desc: nameSort ? isCurrentlyAsc : false },
                ])
              }}
            >
              Name
            </span>
            <span>/</span>
            <span
              className='cursor-pointer'
              onClick={() => column.toggleSorting()}
            >
              score
            </span>
          </div>
        ),
        sortingFn: 'alphanumeric',
        cell: (info) => {
          return (
            <div className='flex flex-col gap-1'>
              <TeamMemberLink
                characterReference={info.row.original}
                iconType='class'
              />
              <div className='text-success-foreground text-base'>
                {info.row.original.totalScore > 0 ? (
                  <span>
                    {info.row.original.totalScore.toLocaleString('en-US', {
                      maximumFractionDigits:
                        valueDisplay === 'percentage' ? 2 : 0,
                      minimumFractionDigits:
                        valueDisplay === 'percentage' ? 2 : 0,
                    })}
                    {valueDisplay === 'percentage' ? '%' : null}
                  </span>
                ) : (
                  ''
                )}
              </div>
            </div>
          )
        },
      },
      ...teamWishlist.encounters.map((encounter) => ({
        header: ({
          column,
        }: HeaderContext<CharacterEncounterWishOverview, unknown>) => (
          <div
            className='flex items-center gap-2 py-2 cursor-pointer'
            onClick={() => column.toggleSorting()}
          >
            <img
              src={`https://data.wowaudit.com/img/${encounter.background}.jpg`}
              className='w-8 h-8 min-w-8 min-h-8 rounded-full'
            />
            <span className='truncate'>{encounter.name}</span>
          </div>
        ),
        id: `encounters.${encounter.id}`,
        size: Math.floor(1080 / teamWishlist.encounters.length),
        accessorFn: (row: CharacterEncounterWishOverview) =>
          (row.encounters.find((e) => e.id === encounter.id)?.items ?? []).map(
            (item) =>
              valueDisplay === 'percentage'
                ? item.score_by_spec.total.percentage
                : item.score_by_spec.total.score,
          ),
        cell: (info: CellContext<CharacterEncounterWishOverview, unknown>) => {
          const characterEncounter = info.row.original.encounters.find(
            (e) => e.id === encounter.id,
          )

          const items =
            info.row.original.encounters.find((e) => e.id === encounter.id)
              ?.items ?? []

          const priorityColor = items.some((item) => item.edited)
            ? 'text-warning-foreground'
            : items.some((item) => item.outdated)
            ? 'text-destructive-foreground'
            : 'text-success-foreground'

          if (!characterEncounter?.items.length) {
            return <div>-</div>
          }

          const sumValue = sum(
            (info.getValue() as number[]).filter((value) => !isNaN(value)),
          )

          const manualUpgrades = (info.getValue() as string[]).filter(
            (value) => typeof value === 'string',
          )

          return (
            <div className='flex flex-col gap-1'>
              <div className={`${priorityColor} flex items-center gap-1`}>
                <span>
                  {sumValue?.toLocaleString('en-US', {
                    maximumFractionDigits:
                      valueDisplay === 'percentage' ? 2 : 0,
                    minimumFractionDigits:
                      valueDisplay === 'percentage' ? 2 : 0,
                  })}
                  {valueDisplay === 'percentage' && '%'}
                </span>
                {manualUpgrades.map((upgrade) => (
                  <WishLegacyPriorityDot
                    key={upgrade}
                    priority={(upgrade as WishLegacyPriority) ?? 'none'}
                  />
                ))}
              </div>
              <div className='flex items-center gap-1 flex-wrap'>
                {characterEncounter.items.map((item) => (
                  <TeamWishTooltip
                    key={item.id}
                    wishlist={item}
                    itemInfo={{ ...item, wishlists: [] }}
                  >
                    <BlizzardIcon
                      className='cursor-pointer'
                      key={item.id}
                      iconName={item.icon}
                      size='medium'
                    />
                  </TeamWishTooltip>
                ))}
              </div>
            </div>
          )
        },
      })),
    ],
    [teamWishlist.encounters, valueDisplay],
  )

  return (
    <>
      <DataTable
        columns={columns}
        className='mt-6'
        cellClassName='align-top'
        data={tableData}
        includeHeader={true}
        visibilityState={{
          name: false,
        }}
        sortingState={[{ id: 'totalScore', desc: true }]}
        emptyMessage={() => (
          <Trans
            i18nKey={
              team.members?.length
                ? 'dashboard.team.table-all-invisible'
                : accessLevel === 'admin'
                ? 'dashboard.team.table-no-members-admin'
                : 'dashboard.team.table-no-members'
            }
            components={
              accessLevel === 'admin'
                ? {
                    link: <Link to={`${team.path}/settings#roster`} />,
                    modal: (
                      <a
                        className='text-primary cursor-pointer'
                        onClick={() => setAddingMembers(true)}
                      />
                    ),
                  }
                : undefined
            }
          />
        )}
      />

      {addingMembers && (
        <AddTeamMemberModal open={addingMembers} setOpen={setAddingMembers} />
      )}
    </>
  )
}

export default TeamWishlistTable
